<template>
  <RevCard class="flex-1 p-24">
    <RevTag
      v-if="shouldDisplayEmptyState"
      class="mb-8"
      :label="i18n(translations.toDoLabel)"
      variant="warning"
    />
    <WithSkeletonLoader :is-loading="pending">
      <div class="grow">
        <h2 class="heading-2 mb-8">{{ i18n(translations.title) }}</h2>
      </div>

      <div v-if="shouldDisplayEmptyState" class="flex h-full flex-col">
        <div class="flex items-center">
          <IconArrowRight />

          <div class="body-1 ml-12">
            {{ i18n(translations.accountNumberTitle) }}
          </div>
        </div>

        <div class="flex items-center">
          <IconArrowRight />

          <div class="body-1 ml-12">
            {{ i18n(translations.routingNumberTitle) }}
          </div>
        </div>

        <div class="mt-24">
          <RevButton
            full-width="never"
            variant="primary"
            @click="handleEditModalOpen"
          >
            {{ i18n(translations.addDetailsButton) }}
          </RevButton>
        </div>
      </div>

      <div v-if="!shouldDisplayEmptyState" class="flex h-full flex-col">
        <div class="mb-16">
          <div class="caption mb-1">
            {{ i18n(translations.accountNumberTitle) }}
          </div>

          <div class="body-1 flex items-center justify-between">
            <span>
              {{ bankingDetails?.accountNumber }}
            </span>
          </div>
        </div>

        <div class="mb-16">
          <div class="caption mb-4">
            {{ i18n(translations.routingNumberTitle) }}
          </div>

          <div class="body-1 flex items-center justify-between">
            <span>
              {{ bankingDetails?.routingNumber }}
            </span>
          </div>
        </div>

        <div class="mt-24">
          <RevButton
            full-width="never"
            :icon="IconEdit"
            variant="secondary"
            @click="handleEditModalOpen"
          >
            {{ i18n(translations.editButton) }}
          </RevButton>
        </div>
      </div>
    </WithSkeletonLoader>
  </RevCard>

  <BankingDetailsFormModal :banking-details @update-banking-details="refresh" />
</template>

<script setup lang="ts">
import { computed } from 'vue'

import { sellerProfileAPI } from '@backmarket/http-api'
import { useHttpLazyFetch } from '@backmarket/nuxt-module-http/useHttpLazyFetch'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { isEmpty } from '@backmarket/utils/object/isEmpty'
import { RevButton } from '@ds/components/Button'
import { RevCard } from '@ds/components/Card'
import { openModal } from '@ds/components/ModalBase'
import { RevTag } from '@ds/components/Tag'
import { IconArrowRight } from '@ds/icons/IconArrowRight'
import { IconEdit } from '@ds/icons/IconEdit'

import WithSkeletonLoader from '../../../../components/WithSkeletonLoader/WithSkeletonLoader.vue'

import translations from './BankingDetails.translations'
import { MODAL_NAME } from './components/BankingDetailsFormModal/BankingDetailsFormModal.constants'
import BankingDetailsFormModal from './components/BankingDetailsFormModal/BankingDetailsFormModal.vue'

const i18n = useI18n()

const {
  data: bankingDetailsData,
  pending,
  refresh,
} = useHttpLazyFetch(sellerProfileAPI.getProfileViewBankingDetails, {
  queryParams: { views: 'bankingInformation' },
})

const bankingDetails = computed(
  () => bankingDetailsData.value?.bankingInformation,
)

const shouldDisplayEmptyState = computed(() => {
  return (
    isEmpty(bankingDetails.value?.accountNumber) &&
    isEmpty(!bankingDetails.value?.routingNumber)
  )
})

function handleEditModalOpen() {
  openModal(MODAL_NAME)
}
</script>
